import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLoader, FiCheck, FiAlertTriangle, FiMapPin, FiGlobe, FiSearch, FiX } from 'react-icons/fi';
import authService from '../../../services/authService';
import styles from '../Auth.module.css';

/**
 * InstitutionVerifier - Real-time institution detection with ROR API
 * Shows institution details when detected, manual entry when not found
 */
const InstitutionVerifier = ({
  email,
  onVerificationComplete,
  onManualInstitutionChange,
  manualInstitutionName,
  disabled = false,
}) => {
  const [verificationState, setVerificationState] = useState({
    isVerifying: false,
    isVerified: false,
    institution: null,
    isInstitutional: false,
    suggestManualEntry: false,
    error: null,
    message: '',
  });
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce verification
  const verifyEmail = useCallback(async (emailToVerify) => {
    if (!emailToVerify || !emailToVerify.includes('@')) {
      setVerificationState({
        isVerifying: false,
        isVerified: false,
        institution: null,
        isInstitutional: false,
        suggestManualEntry: false,
        error: null,
        message: '',
      });
      onVerificationComplete?.(null);
      return;
    }

    setVerificationState(prev => ({ ...prev, isVerifying: true, error: null }));

    try {
      const result = await authService.verifyInstitutionEmail(emailToVerify);
      
      setVerificationState({
        isVerifying: false,
        isVerified: true,
        institution: result.institution,
        isInstitutional: result.isInstitutional,
        suggestManualEntry: result.suggestManualEntry || false,
        error: null,
        message: result.message || '',
      });

      onVerificationComplete?.(result);
    } catch (error) {
      setVerificationState({
        isVerifying: false,
        isVerified: false,
        institution: null,
        isInstitutional: false,
        suggestManualEntry: true,
        error: error.message || 'Verification failed',
        message: '',
      });
      onVerificationComplete?.(null);
    }
  }, [onVerificationComplete]);

  // Verify when email changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      verifyEmail(email);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [email, verifyEmail]);

  // Institution search
  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const result = await authService.searchInstitutions(query);
      setSearchResults(result.institutions || []);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showSearch && searchQuery.length >= 2) {
        handleSearch(searchQuery);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, showSearch]);

  const selectInstitution = (institution) => {
    onManualInstitutionChange?.(institution.name);
    setVerificationState(prev => ({
      ...prev,
      institution: institution,
      suggestManualEntry: false,
    }));
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const { isVerifying, institution, isInstitutional, suggestManualEntry, message } = verificationState;

  return (
    <div className={styles['au-institution-verifier']}>
      <AnimatePresence mode="wait">
        {/* Loading State */}
        {isVerifying && (
          <motion.div
            className={styles['au-institution-status']}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            key="verifying"
          >
            <FiLoader className={styles['au-institution-status__spinner']} />
            <span className={styles['au-institution-status__text']}>
              Verifying institution...
            </span>
          </motion.div>
        )}

        {/* Institution Detected */}
        {!isVerifying && institution && (
          <motion.div
            className={`${styles['au-institution-card']} ${styles['au-institution-card--success']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            key="detected"
          >
            <div className={styles['au-institution-card__header']}>
              <FiCheck className={styles['au-institution-card__icon']} />
              <span className={styles['au-institution-card__badge']}>Verified</span>
            </div>
            <h4 className={styles['au-institution-card__name']}>
              {institution.name}
            </h4>
            <div className={styles['au-institution-card__details']}>
              {institution.country && (
                <span className={styles['au-institution-card__detail']}>
                  <FiMapPin size={12} />
                  {institution.country}
                </span>
              )}
              {institution.type && (
                <span className={styles['au-institution-card__detail']}>
                  <FiGlobe size={12} />
                  {institution.type.charAt(0).toUpperCase() + institution.type.slice(1)}
                </span>
              )}
            </div>
          </motion.div>
        )}

        {/* Not Institutional Email Warning */}
        {!isVerifying && !isInstitutional && email && email.includes('@') && (
          <motion.div
            className={`${styles['au-institution-card']} ${styles['au-institution-card--warning']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            key="warning"
          >
            <div className={styles['au-institution-card__header']}>
              <FiAlertTriangle className={styles['au-institution-card__icon']} />
              <span className={styles['au-institution-card__badge']}>Personal Email</span>
            </div>
            <p className={styles['au-institution-card__message']}>
              {message || 'We recommend using an institutional email for verification.'}
            </p>
          </motion.div>
        )}

        {/* Manual Entry Suggestion */}
        {!isVerifying && suggestManualEntry && isInstitutional && !institution && (
          <motion.div
            className={styles['au-institution-manual']}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            key="manual"
          >
            <p className={styles['au-institution-manual__text']}>
              We couldn't auto-detect your institution. Please enter it manually:
            </p>
            
            {!showSearch ? (
              <div className={styles['au-institution-manual__input-group']}>
                <input
                  type="text"
                  value={manualInstitutionName || ''}
                  onChange={(e) => onManualInstitutionChange?.(e.target.value)}
                  placeholder="Enter your institution name"
                  className={styles['au-input']}
                  disabled={disabled}
                />
                <button
                  type="button"
                  className={styles['au-institution-manual__search-btn']}
                  onClick={() => setShowSearch(true)}
                  disabled={disabled}
                >
                  <FiSearch size={16} />
                  Search
                </button>
              </div>
            ) : (
              <div className={styles['au-institution-search']}>
                <div className={styles['au-institution-search__input-wrapper']}>
                  <FiSearch className={styles['au-institution-search__icon']} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search institutions..."
                    className={styles['au-input']}
                    autoFocus
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    className={styles['au-institution-search__close']}
                    onClick={() => {
                      setShowSearch(false);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    <FiX size={16} />
                  </button>
                </div>
                
                {isSearching && (
                  <div className={styles['au-institution-search__loading']}>
                    <FiLoader className={styles['au-institution-status__spinner']} />
                    Searching...
                  </div>
                )}
                
                {!isSearching && searchResults.length > 0 && (
                  <ul className={styles['au-institution-search__results']}>
                    {searchResults.map((inst, index) => (
                      <li key={inst.rorId || index}>
                        <button
                          type="button"
                          className={styles['au-institution-search__result']}
                          onClick={() => selectInstitution(inst)}
                        >
                          <span className={styles['au-institution-search__result-name']}>
                            {inst.name}
                          </span>
                          {inst.country && (
                            <span className={styles['au-institution-search__result-country']}>
                              {inst.country}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                
                {!isSearching && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <p className={styles['au-institution-search__no-results']}>
                    No institutions found. You can enter your institution name manually above.
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InstitutionVerifier;
